import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const TestimonialsSection = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const testimonials = [
    {
      id: 1,
      name: "Sarah Johnson",
      role: "Tech Entrepreneur",
      company: "InnovateTech Solutions",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&h=100&fit=crop&crop=face",
      rating: 5,
      content: `ElectroShop Pro has been my go-to destination for premium electronics. The transparency in pricing and real-time inventory updates give me confidence in every purchase. Their customer service is exceptional, and the product quality consistently exceeds expectations.`,
      purchasedProduct: "MacBook Pro M3",
      verifiedPurchase: true,
      location: "San Francisco, CA"
    },
    {
      id: 2,
      name: "Michael Chen",
      role: "Software Developer",
      company: "Google",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face",
      rating: 5,
      content: `The dark-mode interface and technical specifications detail are perfect for someone like me who appreciates both aesthetics and functionality. I've purchased multiple items and each experience has been seamless. The admin dashboard transparency is a game-changer.`,
      purchasedProduct: "iPhone 15 Pro Max",
      verifiedPurchase: true,
      location: "Seattle, WA"
    },
    {
      id: 3,
      name: "Emily Rodriguez",
      role: "Creative Director",
      company: "Design Studio Pro",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face",
      rating: 5,
      content: `What sets ElectroShop Pro apart is their commitment to quality and customer experience. The product comparisons, detailed reviews, and fast shipping make it easy to make informed decisions. I recommend them to all my colleagues.`,
      purchasedProduct: "iPad Pro M4",
      verifiedPurchase: true,
      location: "Austin, TX"
    },
    {
      id: 4,
      name: "David Kim",
      role: "Gaming Enthusiast",
      company: "Freelancer",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face",
      rating: 5,
      content: `As a tech enthusiast, I'm very particular about where I shop for electronics. ElectroShop Pro's curated selection, competitive pricing, and excellent customer support have made them my preferred choice. The mobile experience is also top-notch.`,
      purchasedProduct: "Sony WH-1000XM5",
      verifiedPurchase: true,
      location: "Los Angeles, CA"
    },
    {
      id: 5,
      name: "Lisa Thompson",
      role: "Business Analyst",
      company: "Fortune 500 Corp",
      avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&h=100&fit=crop&crop=face",
      rating: 5,
      content: `The business transparency and location verification give me peace of mind when making large purchases. Their SSL security and professional approach to e-commerce set them apart from competitors. Highly recommended for business professionals.`,
      purchasedProduct: "Dell XPS 13",
      verifiedPurchase: true,
      location: "New York, NY"
    }
  ];

  const stats = [
    { label: "Happy Customers", value: "50,000+", icon: "Users" },
    { label: "Products Sold", value: "125,000+", icon: "Package" },
    { label: "Customer Rating", value: "4.9/5", icon: "Star" },
    { label: "Countries Served", value: "25+", icon: "Globe" }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials?.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [testimonials?.length]);

  return (
    <section className="py-20 bg-gradient-to-b from-background to-card/30 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-20 w-64 h-64 bg-primary rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-secondary rounded-full blur-3xl"></div>
      </div>
      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-accent/10 border border-accent/30 rounded-full px-4 py-2 mb-6">
            <Icon name="MessageSquare" size={16} className="text-accent" />
            <span className="text-sm font-mono text-accent">CUSTOMER TESTIMONIALS</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="text-foreground">What Our</span>
            <br />
            <span className="bg-gradient-to-r from-accent to-primary bg-clip-text text-transparent">
              Customers Say
            </span>
          </h2>
          
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Real feedback from real customers who trust ElectroShop Pro for their technology needs. 
            Join thousands of satisfied customers worldwide.
          </p>
        </motion.div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-16"
        >
          {stats?.map((stat, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.05 }}
              className="text-center"
            >
              <div className="holographic-card p-6 rounded-2xl">
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-primary/10 border border-primary/30 rounded-full">
                    <Icon name={stat?.icon} size={24} className="text-primary" />
                  </div>
                </div>
                <div className="text-3xl font-bold text-foreground mb-2">{stat?.value}</div>
                <div className="text-sm text-muted-foreground">{stat?.label}</div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Main Testimonial Display */}
        <div className="max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentTestimonial}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.6 }}
              className="holographic-card p-8 lg:p-12 rounded-3xl relative"
            >
              {/* Quote Icon */}
              <div className="absolute top-6 left-6 opacity-20">
                <Icon name="Quote" size={48} className="text-primary" />
              </div>

              {/* Testimonial Content */}
              <div className="relative z-10">
                {/* Rating */}
                <div className="flex items-center justify-center space-x-1 mb-6">
                  {[...Array(testimonials?.[currentTestimonial]?.rating)]?.map((_, i) => (
                    <Icon
                      key={i}
                      name="Star"
                      size={20}
                      className="text-accent fill-current"
                    />
                  ))}
                </div>

                {/* Content */}
                <blockquote className="text-xl lg:text-2xl text-foreground leading-relaxed text-center mb-8 font-medium">
                  "{testimonials?.[currentTestimonial]?.content}"
                </blockquote>

                {/* Customer Info */}
                <div className="flex flex-col lg:flex-row items-center justify-center space-y-4 lg:space-y-0 lg:space-x-6">
                  {/* Avatar */}
                  <div className="relative">
                    <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-primary/30">
                      <Image
                        src={testimonials?.[currentTestimonial]?.avatar}
                        alt={testimonials?.[currentTestimonial]?.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {testimonials?.[currentTestimonial]?.verifiedPurchase && (
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-success rounded-full flex items-center justify-center border-2 border-background">
                        <Icon name="Check" size={12} color="white" />
                      </div>
                    )}
                  </div>

                  {/* Details */}
                  <div className="text-center lg:text-left">
                    <div className="font-bold text-foreground text-lg">
                      {testimonials?.[currentTestimonial]?.name}
                    </div>
                    <div className="text-primary font-medium">
                      {testimonials?.[currentTestimonial]?.role}
                    </div>
                    <div className="text-muted-foreground text-sm">
                      {testimonials?.[currentTestimonial]?.company}
                    </div>
                    <div className="flex items-center justify-center lg:justify-start space-x-2 mt-2">
                      <Icon name="MapPin" size={14} className="text-muted-foreground" />
                      <span className="text-muted-foreground text-sm">
                        {testimonials?.[currentTestimonial]?.location}
                      </span>
                    </div>
                  </div>

                  {/* Purchase Info */}
                  <div className="flex items-center space-x-2 bg-success/10 border border-success/30 rounded-full px-4 py-2">
                    <Icon name="ShoppingBag" size={16} className="text-success" />
                    <span className="text-sm text-success font-medium">
                      Purchased: {testimonials?.[currentTestimonial]?.purchasedProduct}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation Dots */}
          <div className="flex justify-center space-x-2 mt-8">
            {testimonials?.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentTestimonial(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentTestimonial
                    ? 'bg-primary scale-125' :'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                }`}
              />
            ))}
          </div>

          {/* Navigation Arrows */}
          <div className="flex justify-center space-x-4 mt-6">
            <button
              onClick={() => setCurrentTestimonial((prev) => (prev - 1 + testimonials?.length) % testimonials?.length)}
              className="p-3 bg-card border border-border rounded-full hover:bg-muted/50 transition-colors"
            >
              <Icon name="ChevronLeft" size={20} />
            </button>
            <button
              onClick={() => setCurrentTestimonial((prev) => (prev + 1) % testimonials?.length)}
              className="p-3 bg-card border border-border rounded-full hover:bg-muted/50 transition-colors"
            >
              <Icon name="ChevronRight" size={20} />
            </button>
          </div>
        </div>

        {/* Trust Indicators */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="flex flex-wrap justify-center items-center gap-8 mt-16 pt-16 border-t border-border"
        >
          <div className="flex items-center space-x-2">
            <div className="trust-seal p-2 bg-success/10 border border-success/30 rounded-lg">
              <Icon name="Shield" size={20} color="var(--color-success)" />
            </div>
            <span className="text-sm text-muted-foreground">SSL Secured</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="trust-seal p-2 bg-primary/10 border border-primary/30 rounded-lg">
              <Icon name="Award" size={20} color="var(--color-primary)" />
            </div>
            <span className="text-sm text-muted-foreground">Verified Reviews</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="trust-seal p-2 bg-secondary/10 border border-secondary/30 rounded-lg">
              <Icon name="Truck" size={20} color="var(--color-secondary)" />
            </div>
            <span className="text-sm text-muted-foreground">Fast Shipping</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="trust-seal p-2 bg-accent/10 border border-accent/30 rounded-lg">
              <Icon name="Headphones" size={20} color="var(--color-accent)" />
            </div>
            <span className="text-sm text-muted-foreground">24/7 Support</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;